import { useState, useEffect } from 'react';
import contactService from '../../../utils/contactService';
import { useContactModal } from '../../../components/ContactModal';
import './SupportTickets.css';

export default function SupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, open, resolved, closed
  const [expandedTicket, setExpandedTicket] = useState(null);
  const { openContactModal, ContactModalComponent } = useContactModal();

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const { data, error } = await contactService.getUserContacts();

      if (error) {
        console.error('Error loading tickets:', error);
        return;
      }

      setTickets(data || []);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'open': 'status-badge-open',
      'in_progress': 'status-badge-progress',
      'resolved': 'status-badge-resolved',
      'closed': 'status-badge-closed'
    };
    return statusMap[status] || 'status-badge-default';
  };

  const getStatusDisplay = (status) => {
    const displayMap = {
      'open': 'Open',
      'in_progress': 'In Progress',
      'resolved': 'Resolved',
      'closed': 'Closed'
    };
    return displayMap[status] || status;
  };

  const getPriorityBadgeClass = (priority) => {
    const priorityMap = {
      'urgent': 'priority-badge-urgent',
      'high': 'priority-badge-high',
      'medium': 'priority-badge-medium',
      'low': 'priority-badge-low'
    };
    return priorityMap[priority] || 'priority-badge-default';
  };

  const getPriorityDisplay = (priority) => {
    return priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'Normal';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filter === 'all') return true;
    if (filter === 'open') return ticket.status === 'open' || ticket.status === 'in_progress';
    return ticket.status === filter;
  });

  const toggleTicket = (ticketId) => {
    setExpandedTicket(expandedTicket === ticketId ? null : ticketId);
  };

  if (loading) {
    return (
      <div className="support-tickets-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading your support tickets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="support-tickets-container">
      <div className="support-tickets-header">
        <div className="header-content">
          <h2>Support Tickets</h2>
          <p>View and track your support requests</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => openContactModal()}
        >
          + New Ticket
        </button>
      </div>

      <div className="ticket-filters">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({tickets.length})
        </button>
        <button
          className={`filter-btn ${filter === 'open' ? 'active' : ''}`}
          onClick={() => setFilter('open')}
        >
          Open ({tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length})
        </button>
        <button
          className={`filter-btn ${filter === 'resolved' ? 'active' : ''}`}
          onClick={() => setFilter('resolved')}
        >
          Resolved ({tickets.filter(t => t.status === 'resolved').length})
        </button>
        <button
          className={`filter-btn ${filter === 'closed' ? 'active' : ''}`}
          onClick={() => setFilter('closed')}
        >
          Closed ({tickets.filter(t => t.status === 'closed').length})
        </button>
      </div>

      {filteredTickets.length === 0 ? (
        <div className="empty-state">
          <svg className="empty-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          <h3>No tickets found</h3>
          <p>
            {filter === 'all'
              ? "You haven't submitted any support tickets yet."
              : `You don't have any ${filter} tickets.`
            }
          </p>
          <button
            className="btn-secondary"
            onClick={() => openContactModal()}
          >
            Submit Your First Ticket
          </button>
        </div>
      ) : (
        <div className="tickets-list">
          {filteredTickets.map(ticket => (
            <div
              key={ticket.id}
              className={`ticket-card ${expandedTicket === ticket.id ? 'expanded' : ''}`}
            >
              <div
                className="ticket-header"
                onClick={() => toggleTicket(ticket.id)}
              >
                <div className="ticket-info">
                  <div className="ticket-title-row">
                    <h3>{ticket.subject}</h3>
                    <div className="ticket-badges">
                      <span className={`status-badge ${getStatusBadgeClass(ticket.status)}`}>
                        {getStatusDisplay(ticket.status)}
                      </span>
                      <span className={`priority-badge ${getPriorityBadgeClass(ticket.priority)}`}>
                        {getPriorityDisplay(ticket.priority)}
                      </span>
                    </div>
                  </div>
                  <div className="ticket-meta">
                    <span className="meta-item">
                      <svg className="meta-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      {ticket.issue_type?.replace(/_/g, ' ')}
                    </span>
                    <span className="meta-item">
                      <svg className="meta-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {formatDate(ticket.created_at)}
                    </span>
                    {ticket.updated_at !== ticket.created_at && (
                      <span className="meta-item">
                        <svg className="meta-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Updated {formatDate(ticket.updated_at)}
                      </span>
                    )}
                  </div>
                </div>
                <svg
                  className={`expand-icon ${expandedTicket === ticket.id ? 'rotated' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {expandedTicket === ticket.id && (
                <div className="ticket-details">
                  <div className="ticket-section">
                    <h4>Your Message</h4>
                    <div className="message-content">
                      {ticket.message}
                    </div>
                  </div>

                  {ticket.admin_note && (
                    <div className="ticket-section admin-response">
                      <h4>
                        <svg className="section-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Support Team Response
                      </h4>
                      <div className="message-content">
                        {ticket.admin_note}
                      </div>
                    </div>
                  )}

                  {ticket.metadata && Object.keys(ticket.metadata).length > 0 && (
                    <div className="ticket-section metadata-section">
                      <h4>Additional Information</h4>
                      <div className="metadata-grid">
                        {ticket.metadata.sourcePage && (
                          <div className="metadata-item">
                            <span className="metadata-label">Source Page:</span>
                            <span className="metadata-value">{ticket.metadata.sourcePage}</span>
                          </div>
                        )}
                        {ticket.metadata.browser && (
                          <div className="metadata-item">
                            <span className="metadata-label">Browser:</span>
                            <span className="metadata-value">{ticket.metadata.browser}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="ticket-id">
                    <span>Ticket ID: {ticket.id}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {ContactModalComponent}
    </div>
  );
}
